import Form from "@/components/Form";
import PageHeader from "@/components/PageHeader";
import SaveButton from "@/components/SaveButton";
import Spinner from "@/components/Spinner";
import Title from "@/components/Title";

import Field from "@/components/Field";
import RecordView from "@/components/record/RecordView";
import { TextareaWithLimitInfo } from "@/components/TextareaWithLimitInfo";
import { Module, PATH_PAYROLL_AGGREGATION } from "@/constants";
import { updatePayrollAggregate } from "@/schemas/payrollAggregate";
import { PayrollsService } from "@/services/payrolls";
import { PayrollAggregate } from "@/types/payrollAggregate";
import { z } from "@/zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { memo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";
import { usePayrollBreadcrumbs } from "../hooks/usePayrollBreadcrumbs";
import { usePayrollParams } from "../hooks/usePayrollParams";
import { usePayrollPermissions } from "../hooks/usePayrollPermissions";
import { usePayrollQuery } from "../hooks/usePayrollQuery";
import { useUpdatePayrollAggregationMutation } from "../hooks/useUpdatePayrollAggregationMutation";
import { useValueRenderer } from "../../../hooks/useValueRenderer";
import { updatePayrollAggregateDetailsFormSchema } from "../schema";
import { PayrollAggregationRecordView } from "./PayrollAggregationRecordView";
import { NumberInput } from "@/components/ui/NumberInput";
import { formatNumber } from "@/lib/utils/formatters";

type FormData = z.infer<typeof updatePayrollAggregate>;

const defaultValues = {
  deduction_percentage: null,
  administrative_fee: null,
  deduction: null,
  addition: null,
  comment: null,
};

export const PayrollAggregationDetail = memo(() => {
  const { t } = useTranslation();
  const { payrollId, entryId } = usePayrollParams();
  const payrollQuery = usePayrollQuery(payrollId);
  const permissions = usePayrollPermissions();
  const mutation = useUpdatePayrollAggregationMutation();

  const breadcrumbs = usePayrollBreadcrumbs([
    {
      label: t("payrolls.views.aggregation.title"),
      href: generatePath(PATH_PAYROLL_AGGREGATION, {
        payrollId,
      }),
    },
    {
      label: `${t("payrolls.details.title")}`,
    },
  ]);

  const entryQuery = useQuery<PayrollAggregate>({
    queryKey: [Module.Payrolls, payrollId, "aggregation", entryId],
    queryFn: () => PayrollsService.getAggregation(entryId),
    enabled: payrollQuery.isSuccess,
  });

  const form = useForm<FormData>({
    resolver: zodResolver(updatePayrollAggregateDetailsFormSchema),
    defaultValues,
    disabled:
      !permissions.canUpdateAggregate ||
      mutation.isPending ||
      entryQuery.isFetching,
  });

  useEffect(() => {
    if (entryQuery.data === undefined) {
      return;
    }
    const { id, ...rest } = entryQuery?.data || {};
    form.reset(rest);
  }, [entryQuery.data, form]);

  const handleSubmit = useCallback(
    (values: FormData) => {
      return mutation.mutateAsync(values);
    },
    [mutation],
  );

  const renderValue = useValueRenderer();

  const entry = entryQuery.data;

  if (payrollQuery.isLoading || entryQuery.isLoading) {
    return <Spinner className="w-full h-full" />;
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbs} />

      <Title>{t("payrolls.views.aggregation.title")}</Title>

      <PayrollAggregationRecordView />

      <Title level={4}>{t("payrolls.details.title")}</Title>

      <Form form={form} handleSubmit={handleSubmit}>
        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.basic"
          header={() => t("payrolls.fieldGroups.basic")}
          isOpen={true}
        >
          <RecordView.Field
            name="period"
            label={t("payrolls.fields.period")}
            renderer={() => renderValue(entry?.period)}
          />
          <RecordView.Field
            name="contractor"
            label={t("payrolls.fields.contractor")}
            renderer={() => renderValue(entry?.contractor)}
          />
          <RecordView.Field
            name="agreement_company"
            label={t("payrolls.fields.agreement_company")}
            renderer={() => renderValue(entry?.agreement_company)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.contractor"
          header={() => t("payrolls.fieldGroups.contractor")}
          isOpen={true}
        >
          <RecordView.Field
            name="personal_id_number"
            label={t("payrolls.fields.personal_id_number")}
            renderer={() => renderValue(entry?.personal_id_number)}
          />
          <RecordView.Field
            name="tax_status_final"
            label={t("payrolls.fields.tax_status_final")}
            renderer={() => renderValue(entry?.tax_status_final, "taxStatus")}
          />
          <RecordView.Field
            name="is_employed"
            label={t("payrolls.fields.is_employed")}
            renderer={() => renderValue(entry?.is_employed, "booleanValues")}
          />
          <RecordView.Field
            name="is_student"
            label={t("payrolls.fields.is_student")}
            renderer={() => renderValue(entry?.is_student, "booleanValues")}
          />
          <RecordView.Field
            name="citizenship"
            label={t("payrolls.fields.citizenship")}
            renderer={() => renderValue(entry?.citizenship)}
          />
          <RecordView.Field
            name="student_id_valid_until"
            label={t("payrolls.fields.student_id_valid_until")}
            renderer={() => renderValue(entry?.student_id_valid_until)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.financial"
          header={() => t("payrolls.fieldGroups.financial")}
          isOpen={true}
        >
          <RecordView.Field
            name="aggregated_amount"
            label={t("payrolls.fields.aggregated_amount")}
            renderer={() => formatNumber(entry?.aggregated_amount)}
          />
          <Field
            name="deduction_percentage"
            label={t("payrolls.fields.deduction_percentage")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="administrative_fee"
            label={t("payrolls.fields.administrative_fee")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="deduction"
            label={t("payrolls.fields.deduction")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <Field
            name="addition"
            label={t("payrolls.fields.addition")}
            render={({ field }) => <NumberInput {...field} />}
          />
          <RecordView.Field
            name="amount_to_pay"
            label={t("payrolls.fields.amount_to_pay")}
            renderer={() => formatNumber(entry?.amount_to_pay)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.agreements"
          header={() => t("payrolls.fieldGroups.agreements")}
          isOpen={true}
        >
          <RecordView.Field
            name="agreement_company"
            label={t("payrolls.fields.agreement_company")}
            renderer={() => renderValue(entry?.agreement_company)}
          />
        </RecordView>

        <RecordView
          query={entryQuery}
          translationKey="payrolls.fieldGroups.comments"
          header={() => t("payrolls.fieldGroups.comments")}
          isOpen={true}
        >
          <Field
            name="comment"
            label={t("payrolls.fields.comment_aggregate")}
            render={({ field }) => (
              <TextareaWithLimitInfo
                {...field}
                placeholder={t("payrolls.placeholders.comment")}
                maxLength={250}
              />
            )}
          />
          <RecordView.Field
            name="permanent_comment"
            label={t("payrolls.fields.permanent_comment")}
            renderer={() => renderValue(entry?.permanent_comment)}
          />
        </RecordView>
        <footer className="flex py-2 justify-end gap-6 mt-6">
          {permissions.canUpdateAggregate && <SaveButton />}
        </footer>
      </Form>
    </>
  );
});

PayrollAggregationDetail.displayName = "PayrollAggregationDetail";
